import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  TablePagination,
  CircularProgress,
  Card,
  Tooltip
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as XLSX from 'xlsx';
import EditEoltReportDialog from './EditEoltReportDialog';
import GenerateEoltReportDialog, { TCUHealthData } from './GenerateEoltReportDialog';
import EoltReportViewDialog, { EoltReportData } from './EoltReportViewDialog';
import { useAuth } from "../../hooks/useAuth";
import { useQuery, useLazyQuery, gql } from '@apollo/client';
import SearchIcon from '@mui/icons-material/Search';

const GET_EOLT_REPORTS = gql`
  query GetEoltReports($limit: Int, $offset: Int, $searchTerm: String) {
    getEoltReports(limit: $limit, offset: $offset, searchTerm: $searchTerm) {
      success
      totalCount
      data {
        vin
        imei
        engine_no
        fileName
        createdOn
      }
    }
  }
`;

const CHECK_TCU_HEALTH = gql`
  query CheckTCUHealth($vin: String!) {
    checkTCUHealth(vin: $vin) {
      success
      data {
        vin
        imei
        networkOperator
        boxCoverStatus
        address
        tcuBatlevel
        firmwareVersion
        serverTS
        latitude
        longitude
        gsmSignal
        ticketId
        ignitionTS
        vehBatVol
        fuelTemp
        adblueLevel
        odoMeter
        hourMeter
        dtc
        warningLight
        cleanerChokeIndicator
        lowOilPressure
        oilPassFilter
        dpfLampIndicator
        dpfActiveRegeneration
        dpfInhibitRegeneration
        massPercentage
        waterInFuelLevel
        torqueReduction
        speedReduction
        ecoMode
        engineRmp
        coolantTemp
        oilTemp
        gearIndicator
        createdByName
        createdById
        fileName
        helpButtonStatus
        packetType
      }
      message
    }
  }
`;

const GENERATE_EOLT_REPORT = gql`
  query GenerateEoltReport($vin: String!) {
    generateEoltReport(vin: $vin) {
      success
      fileName
      url
      reportId
      message
    }
  }
`;

const cellStyle = {
  fontSize: '14px',
  padding: '8px',
  width: 'auto',
  borderBottom: '1px solid #e0e0e0',
  borderRight: '1px solid #e0e0e0',
  color: '#000',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const EoltReport = () => {
  const auth = useAuth();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedEolt, setSelectedEolt] = useState<any>(null);
  const [generatedReports, setGeneratedReports] = useState<Record<string, EoltReportData>>({});
  const [exportLoading, setExportLoading] = useState(false);
  const [reportViewDialogOpen, setReportViewDialogOpen] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState<EoltReportData | null>(null);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedRowForGenerate, setSelectedRowForGenerate] = useState<any>(null);
  const [tcuHealthData, setTcuHealthData] = useState<TCUHealthData | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);
  useEffect(() => {
    const delay = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(0);
    }, 500);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const { loading: queryLoading, refetch } = useQuery(GET_EOLT_REPORTS, {
    variables: {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      searchTerm: debouncedSearch || undefined,
    },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data?.getEoltReports?.success) {
        setTableData(data.getEoltReports.data || []);
        setTotalCount(data.getEoltReports.totalCount || 0);
      }
    }
  });

  const [checkTCUHealth, { loading: tcuHealthLoading }] = useLazyQuery(CHECK_TCU_HEALTH, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.checkTCUHealth?.success) {
        setTcuHealthData(data.checkTCUHealth.data);
      } else {
        setTcuHealthData(null);
        alert(data?.checkTCUHealth?.message || "Failed to fetch TCU health data");
      }
    },
    onError: (error) => {
      console.error("Error fetching TCU health:", error);
      setTcuHealthData(null);
    }
  });

  const [generateEolt, { loading: generatingReport }] = useLazyQuery(GENERATE_EOLT_REPORT, {
    fetchPolicy: 'network-only',
  });

  const isLoading = queryLoading;

  const handleGenerateEoltClick = (row: any) => {
    setSelectedRowForGenerate(row);
    setTcuHealthData(null);
    setGenerateDialogOpen(true);
    checkTCUHealth({ variables: { vin: row.vin } });
  };

  const handleConfirmGenerate = async (vin: string) => {
    try {
      const response = await generateEolt({ variables: { vin } });
      if (response.data?.generateEoltReport?.success) {
        const report = response.data.generateEoltReport;
        setGeneratedReports(prev => ({
          ...prev,
          [vin]: {
            reportId: report.reportId || '',
            fileName: report.fileName || '',
            url: report.url || '',
          }
        }));
        setGenerateDialogOpen(false);
        setSelectedRowForGenerate(null);
        setTcuHealthData(null);
        refetch(); 
      } else {
        alert(response.data?.generateEoltReport?.message || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      alert("An error occurred while generating report");
    }
  };

  const handleReportLinkClick = (row: any) => {
    const generatedReport = generatedReports[row.vin];
    if (generatedReport) {
      setSelectedReportData({
        ...generatedReport,
        createdOn: row.createdOn || '',
        createdByName: row.createdByName || '',
      });
    } else {
      setSelectedReportData({
        reportId: row.reportId || '1',
        fileName: row.fileName || '',
        url: row.url || '',
        createdOn: row.createdOn || '',
        createdByName: row.createdByName || '',
      });
    }
    setReportViewDialogOpen(true);
  };

  const handleReportViewDialogClose = () => {
    setReportViewDialogOpen(false);
    setSelectedReportData(null);
  };

  const handleGenerateDialogClose = () => {
    setGenerateDialogOpen(false);
    setSelectedRowForGenerate(null);
    setTcuHealthData(null);
  };

  const handleEdit = (item: any) => {
    setSelectedEolt({
      ...item,
      engineNumber: item.engine_no,
    });
    setOpenEdit(true);
  };

  const refetchData = () => {
    refetch();
  };
  
  const handleExportToExcel = async () => {
  try {
    setExportLoading(true); 

    const { data } = await refetch({
      limit: totalCount,
      offset: 0,
      searchTerm: debouncedSearch || undefined,
    });

    const fullData = data?.getEoltReports?.data || [];

    const formattedData = fullData.map((item: any, index: number) => ({
      'Sr No.': index + 1,
      'IMEI': item.imei,
      'Engine Number': item.engine_no,
      'VIN': item.vin,
      'EOLT Report': (item.fileName || generatedReports[item.vin]) ? 'Generated' : 'NA',
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'EoltReports');

    XLSX.writeFile(workbook, 'eolt_reports.xlsx');

  } catch (error) {
    console.error("Export failed:", error);
  } finally {
    setExportLoading(false); // ✅ STOP loader (always runs)
  }
};

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const tableColumns = [
    "Sr No.",
    "IMEI",
    "Engine Number",
    "VIN",
    "EOLT Report",
    "Manual EOL",
    "Generate EOLT",
    "Action"
  ];

  return (
    <Box sx={{ width: '100%', px: { xs: 0, sm: 0 } }}>
      <Typography variant="h5" fontSize={{ xs: '1.5rem', sm: '2.125rem' }} sx={{ mb: 2, borderRadius: 2 }}>
        EOLT Report
      </Typography>
      <Card sx={{
        width: '100%',
        maxWidth: '100vw',
        mx: 'auto',
        boxShadow: 2,
        borderRadius: 3,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'auto', md: 'calc(100vh - 120px)' },
        bgcolor: '#fff',
        minHeight: 100,
      }}>
        {/* Header controls */}
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          background: '#fff',
          borderTopLeftRadius: 8,
          borderTopRightRadius: 8,
          boxShadow: '0 2px 8px -4px #e0e0e0',
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 2,
          left: 0,
          right: 0,
          width: '100%',
          minWidth: 0,
          overflow: 'visible',
        }}>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }}
            sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}
          >
            <Box
              display="flex"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, flex: 1, maxWidth: { xs: '100%', sm: '40%' } }}
            >
              <TextField
                size="small"
                placeholder="Search VIN"
                variant="outlined"
                sx={{ minWidth: 120, background: '#f5f5f5', flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <IconButton sx={{ ml: 1, color: '#000' }} onClick={handleExportToExcel}>
                <Tooltip title="Export to Excel" placement="top" arrow>
                 {exportLoading ? <CircularProgress size={20} /> : <CloudDownloadIcon />}
                </Tooltip>
              </IconButton>
            </Box>

          </Box>
        </Box>

        {/* Table area */}
        <Box sx={{
          flex: 1,
          minHeight: 0,
          px: { xs: 0.5, sm: 2 },
          py: 2,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                borderRadius: 2,
                width: '100%',
                minWidth: 800,
                background: '#fff',
                overflow: 'visible',
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 800, width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#E60012' }}>
                    {tableColumns.map((header) => (
                      <TableCell
                        key={header}
                        align="center"
                        sx={{
                          backgroundColor: '#E60012',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: { xs: '0.95rem', sm: '1rem' },
                          whiteSpace: 'nowrap',
                          padding: { xs: '4px', sm: '6px' },
                          borderBottom: '1px solid #e0e0e0',
                          borderRight: '1px solid #e0e0e0',
                          position: 'sticky',
                          top: 0,
                          zIndex: 11,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={tableColumns.length} align="center" sx={{ fontSize: '1.1rem', color: '#888', py: 4 }}>
                        No records found
                      </TableCell>
                    </TableRow>
                  ) : (
                    tableData.map((row, idx) => {
                      const hasReport = !!row.fileName || !!generatedReports[row.vin];
                      const reportUrl = generatedReports[row.vin] || '';
                      return (
                        <TableRow
                          key={row.vin + idx}
                          hover
                          sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#f5f5f5' }}
                        >
                          <TableCell align="center" sx={cellStyle}>{(page * rowsPerPage) + idx + 1}</TableCell>
                          <TableCell align="center" sx={cellStyle}>{row.imei}</TableCell>
                          <TableCell align="center" sx={cellStyle}>{row.engine_no}</TableCell>
                          <TableCell align="center" sx={cellStyle}>{row.vin}</TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            {(row.fileName || generatedReports[row.vin]) ? (
                              <Button
                                variant="contained"
                                onClick={() => handleReportLinkClick(row)}
                                sx={{
                                  backgroundColor: '#58B80F1A',
                                  color: '#58B80F',
                                  borderRadius: '12px',
                                  height: '24px',
                                  padding: '0 12px',
                                  fontSize: '12px',
                                  fontWeight: 'normal',
                                  textTransform: 'none',
                                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                  '&:hover': {
                                    backgroundColor: '#58b80f23',
                                  },
                                  border: '1px solid #58B80F',
                                  minWidth: '108px',
                                }}
                              >
                                VIEW REPORT
                              </Button>
                            ) : (
                              'NA'
                            )}
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            <CheckCircleIcon sx={{
                              color: hasReport ? '#58B80F' : '#C0C0C0'
                            }} />
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            <Button
                              variant="contained"
                              onClick={() => handleGenerateEoltClick(row)}
                              disabled={generatingReport || hasReport}
                              sx={{
                                backgroundColor: hasReport ? '#E0E0E0' : '#58B80F1A',
                                color: hasReport ? '#A0A0A0' : '#58B80F',
                                borderRadius: '12px',
                                height: '24px',
                                padding: '0 12px',
                                fontSize: '12px',
                                fontWeight: 'normal',
                                textTransform: 'none',
                                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                '&:hover': {
                                  backgroundColor: hasReport ? '#E0E0E0' : '#58B80F33',
                                },
                                border: `1px solid ${hasReport ? '#C0C0C0' : '#58B80F'}`,
                                minWidth: '108px',
                              }}
                            >
                              {selectedRowForGenerate?.vin === row.vin && tcuHealthLoading
                                ? <CircularProgress size={16} />
                                : 'GENERATE EOLT'}
                            </Button>
                          </TableCell>
                          <TableCell align="center" sx={cellStyle}>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(row)}
                              disabled={hasReport}
                              sx={{
                                color: !hasReport ? '#000' : 'rgba(0, 0, 0, 0.26)',
                                '&:hover': {
                                  backgroundColor: !hasReport ? 'rgba(0, 0, 0, 0.1)' : 'transparent'
                                }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        {/* Pagination */}
        <Box sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 20,
          background: '#fff',
          borderTop: '1px solid #e0e0e0',
          pt: 2,
          mt: 'auto',
          overflowX: 'auto',
        }}>
          <TablePagination
            rowsPerPageOptions={[20, 50, 100, 500]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              minWidth: '600px',
              '.MuiTablePagination-toolbar': {
                flexWrap: 'nowrap',
                justifyContent: { xs: 'center', sm: 'space-between' },
              },
            }}
          />
        </Box>
      </Card>

      <EditEoltReportDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        onSave={() => setOpenEdit(false)}
        eolt={selectedEolt}
        refetch={refetchData}
      />

      {/* Generate EOLT Report Dialog with TCU Health Data */}
      <GenerateEoltReportDialog
        open={generateDialogOpen}
        onClose={handleGenerateDialogClose}
        rowData={selectedRowForGenerate}
        tcuHealthData={tcuHealthData}
        tcuHealthLoading={tcuHealthLoading}
        onConfirmGenerate={handleConfirmGenerate}
        generating={generatingReport}
      />

      {/* EOLT Report View Dialog */}
      <EoltReportViewDialog
        open={reportViewDialogOpen}
        onClose={handleReportViewDialogClose}
        reportData={selectedReportData}
      />
    </Box>
  );
};

export default EoltReport;